import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CheckRideRequestEntity, CheckRideRequestSubStatusAllItems, FlightSchoolOrganizationEntity, PilotExaminerEntity, PilotExaminerStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { DwContainerOptions, formatDateTimeToLocal, getMomentValue } from '@devwareapps/devware-cap';
import { DwTaskItem } from '../../../z-devware/tasks/models/dw-task-item.model';

import * as moment_ from 'moment';
import { ExaminerRepositoryService } from '../../../examiner/services/examiner-repository.service';
import { ExaminerTaskService, ExaminerTaskType } from '../../../examiner/services/examiner-task.service';
import { AirportMetar } from '../../../examiner/models/airport-metar.model';
import { SchoolRepositoryService } from '../../services/school-repository.service';
import { SchoolTaskService } from '../../services/school-task-service.service';
import { AdminManagerService } from '../../../shared/services/admin-manager.service';
import { AppPermissions } from '../../../../meta-data/app-permissions.enum';

const moment = moment_;

@Component({
  selector: 'app-school-dashboard',
  templateUrl: './school-dashboard.component.html',
  styleUrls: ['./school-dashboard.component.scss']
})
export class SchoolDashboardComponent implements OnInit, OnDestroy {
  protected loadMetarData = true;
  protected includeHomeCardDetails = false;
  protected destroy$ = new Subject<void>();
  errorMessage: string;

  tasks: DwTaskItem[] = [];

  examinerName: string;

  calendarContainerOptions: DwContainerOptions = {
    containerHeaderIcon: 'fa fa-calendar',
    containerHeaderBackgroundColor: 'blue',
    useContainerStyling: true,
    showTitle: true
  }

  metarContainerOptions: DwContainerOptions = {
    containerHeaderIcon: 'fa fa-cloud',
    containerHeaderBackgroundColor: 'blue',
    useContainerStyling: true,
    showTitle: true,

  }
  flightSchool: FlightSchoolOrganizationEntity;

  showCalendar: boolean = true;
  metarData: AirportMetar[];
  titleMessage: string = "School Dashboard";

  loading: boolean = true;

  constructor(
    //private examinerRepositoryService: ExaminerRepositoryService,
    private schoolTaskService: SchoolTaskService,
    private schoolRepositoryService: SchoolRepositoryService,
    private adminManagerService: AdminManagerService
  ) { }

  ngOnInit(): void {
    this.loadSchoolStats();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showAll = false;

  loadSchoolStats() {
    this.schoolRepositoryService.getCurrentFlightSchoolStats(false, this.includeHomeCardDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe((flightSchool) => {
        this.flightSchool = flightSchool;

        this.titleMessage = `${this.flightSchool?.FlightSchoolIdDisplay} Dashboard`;

        this.buildSchoolTasks(flightSchool, this.showAll);
        this.onSchoolStatsLoaded(flightSchool);

        this.loading = false;
      }, () => {
        this.errorMessage = 'The school dashboard could not be loaded. Please refresh to try again.';
        this.loading = false;
      });

      if (this.loadMetarData) {
        this.loadSchoolAirportMetarData();
      }
  }

  protected onSchoolStatsLoaded(flightSchool: FlightSchoolOrganizationEntity): void {
    // Extension point for the inline home dashboard.
  }

  protected buildSchoolTasks(flightSchool: FlightSchoolOrganizationEntity, showAll: boolean = false) {
    this.tasks = [];

    const pilexosUser = this.adminManagerService.getPilexosUser();

    const canManageSchoolSettings = pilexosUser.hasPermission(AppPermissions.flightSchoolCanManageSchoolSettings);


    if ((canManageSchoolSettings && !this.isFlightSchoolSetupComplete(flightSchool)) || showAll) {
      this.tasks.push({ iconClass: 'fa fa-building dw-blue', taskTitle: 'Setup School', tooltip: 'Setup the school by adding program types and a headquaters address', navigateUrl: '/school-settings/school-profile' });
    }

    if ((canManageSchoolSettings && this.hasNone(flightSchool?.FlightSchoolBranch)) || showAll) {
      this.tasks.push({ iconClass: 'fa fa-map dw-blue', taskTitle: 'Setup Locations', tooltip: 'Add your flight school locations', navigateUrl: '/school-settings/school-locations' });
    }

    const hasApplicants = (this.hasNone(flightSchool?.FlightSchoolApplicant) == false);
    if (!hasApplicants || showAll) {
      this.tasks.push({ iconClass: 'fa fa-users dw-blue', taskTitle: 'Add Students', tooltip: 'Add your students to get ready for scheduling their checkride', navigateUrl: '/students' });
    } 

    if (hasApplicants) {
      this.schoolRepositoryService.getSchoolRecentSubmittedApplicantStats()
        .pipe(takeUntil(this.destroy$))
        .subscribe((recentCount) => {
          if (recentCount > 0) {
            this.tasks = this.tasks.filter(task => task.taskTitle !== 'No current tasks');
            this.tasks.push({ iconClass: 'fa fa-users dw-blue', taskTitle: `${recentCount} Applicants Recently Submitted`, tooltip: 'Review student applications submitted through your public portal', navigateUrl: '/students?filter=recent_applicants' });
          }
        });
    }

    // const applicantByType = this.schoolRepositoryService.mapStudentApplicants(flightSchool.FlightSchoolApplicant || []);

    // const publicPortalCount = applicantByType.find(a => a.publicPortalSubmission == true)?.count || 0;
    // const internalCount = applicantByType.find(a => a.publicPortalSubmission == false)?.count || 0;

    // if (publicPortalCount > 0) {
    //   this.tasks.push({ iconClass: 'fa fa-globe dw-blue', taskTitle: `${publicPortalCount} recently submitted Applicants`, tooltip: 'Review student applications submitted through your public portal', navigateUrl: '/students/school-applicants?filter=recent_applicants' });
    // }

    this.showCalendar = true;

    this.addCheckrideTasks(flightSchool?.CheckRideRequest || [], showAll);

    if (this.tasks.length == 0) {
      this.tasks.push({ iconClass: 'fa fa-check dw-green', taskTitle: `No current tasks`, tooltip: 'You currently have no pending tasks or upcoming checkrides', navigateUrl: '' });
    }
  }

  private hasAny(array: any[]): boolean {
    return (array || []).length > 0;
  }
 
   private hasNone(array: any[]): boolean {
    return !this.hasAny(array);
  }

  isFlightSchoolSetupComplete(flightSchool: FlightSchoolOrganizationEntity): boolean {

      // Require at least one training program
      const programCount = flightSchool?.FlightSchoolTrainingProgram?.length || 0;

      if (programCount == 0) {
        return false;
      }

      if (!flightSchool?.HeadquatersAddress?.StreetAddress1 || !flightSchool?.HeadquatersAddress?.City || !flightSchool?.HeadquatersAddress?.StateProvinceId || !flightSchool?.HeadquatersAddress?.PostalCode) {
        return false;
      }

      return true;
  }

  private addCheckrideTasks(checkrides: CheckRideRequestEntity[], showAll: boolean) {
    // Get all scheduled checkrides - excludes cancelled, etc
    const scheuledCheckrides = checkrides.filter((checkride) => this.schoolTaskService.isScheduledCheckride(checkride));

    const tasks = this.schoolTaskService.getCheckrideTasks(scheuledCheckrides, showAll);

    this.tasks = [...this.tasks, ...tasks];
  }

  public loadSchoolAirportMetarData() {
    if (new Date().getMonth() == (10 - 1) && new Date().getDate() == (29)) {
      console.warn('Skipping METAR load on Devware Day - October 29th');
      return;
    }

    this.schoolRepositoryService.getCurrentFlightSchoolMetarData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((metarData) => {
        this.metarData = metarData;
      });
  }

}
